/**
 * Dealer's Choice API
 *
 * Mock service for the gamified challenge discovery game.
 * Based on Doc 4: Explore & Discovery and Doc 8: Gamification System
 */

import type {
  DealersChoiceGame,
  DealersChoiceCard,
  DCHistoryEntry,
  FamiliarityZone,
  ApiResponse,
  UUID,
} from '../types'
import { DC_COOLDOWN_HOURS, ZONE_CONFIG } from '../types'
import { mockDb } from './mockDb'
import { safeExecute } from './client'

const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

// Mock challenge pool for dealing
const MOCK_CHALLENGES = [
  { id: 'ch-1', title: 'Urban Photography Walk', category: 'Decorative Arts & Crafts', difficulty: 'beginner' as const, imageUrl: '' },
  { id: 'ch-2', title: 'Cook a Meal from Scratch', category: 'Cooking & Nutrition', difficulty: 'intermediate' as const, imageUrl: '' },
  { id: 'ch-3', title: 'Learn 5 Constellations', category: 'Nature & Environment', difficulty: 'beginner' as const, imageUrl: '' },
  { id: 'ch-4', title: 'Build a Birdhouse', category: 'DIY & Handicrafts', difficulty: 'intermediate' as const, imageUrl: '' },
  { id: 'ch-5', title: 'Run a 5K', category: 'Sports & Fitness', difficulty: 'advanced' as const, imageUrl: '' },
  { id: 'ch-6', title: 'Write a Short Story', category: 'Writing & Storytelling', difficulty: 'intermediate' as const, imageUrl: '' },
  { id: 'ch-7', title: 'Plant a Herb Garden', category: 'Gardening & Horticulture', difficulty: 'beginner' as const, imageUrl: '' },
  { id: 'ch-8', title: 'Learn Basic Origami', category: 'Decorative Arts & Crafts', difficulty: 'beginner' as const, imageUrl: '' },
  { id: 'ch-9', title: 'Volunteer for a Day', category: 'Volunteering & Service Projects', difficulty: 'intermediate' as const, imageUrl: '' },
]

function pickRandom<T>(arr: T[], count: number): T[] {
  const shuffled = [...arr].sort(() => Math.random() - 0.5)
  return shuffled.slice(0, count)
}

/**
 * Dealer's Choice API
 */
export const dealersChoiceApi = {
  async dealCards(): Promise<ApiResponse<DealersChoiceGame>> {
    return safeExecute(() => {
      const userId = getCurrentUserId()
      if (!userId) throw new Error('Not authenticated')

      // Check cooldown
      const lastGame = mockDb.findWhere<DealersChoiceGame>(
        'dealersChoiceGames',
        (g) => g.userId === userId
      ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]

      if (lastGame?.cooldownEndsAt && new Date(lastGame.cooldownEndsAt) > new Date()) {
        throw new Error('Still on cooldown. Try again later.')
      }

      // Deal 3 cards with different zones
      const zones: FamiliarityZone[] = ['comfort_push', 'rarely_tried', 'uncharted']
      const selectedChallenges = pickRandom(MOCK_CHALLENGES, 3)

      const cards: DealersChoiceCard[] = selectedChallenges.map((ch, i) => ({
        challengeId: ch.id,
        challengeTitle: ch.title,
        challengeCategory: ch.category,
        challengeDifficulty: ch.difficulty,
        challengeImageUrl: ch.imageUrl,
        zone: zones[i],
        multiplier: ZONE_CONFIG[zones[i]].multiplier,
        position: i + 1,
      }))

      const cooldownEndsAt = new Date(Date.now() + DC_COOLDOWN_HOURS * 60 * 60 * 1000).toISOString()

      const game = mockDb.insert<DealersChoiceGame>('dealersChoiceGames', {
        userId,
        cards,
        status: 'revealed',
        cooldownEndsAt,
      } as DealersChoiceGame)

      return game
    })
  },

  async acceptCard(gameId: UUID, cardIndex: number): Promise<ApiResponse<DealersChoiceGame>> {
    return safeExecute(() => {
      const game = mockDb.findById<DealersChoiceGame>('dealersChoiceGames', gameId)
      if (!game) throw new Error('Game not found')
      if (game.status !== 'revealed') throw new Error('Cards already decided')

      const card = game.cards[cardIndex]
      if (!card) throw new Error('Invalid card index')

      // Calculate XP (mock: base 100 * multiplier)
      const xpEarned = Math.round(100 * card.multiplier)

      const updated = mockDb.update<DealersChoiceGame>('dealersChoiceGames', gameId, {
        selectedCardIndex: cardIndex,
        status: 'accepted',
        xpEarned,
      } as Partial<DealersChoiceGame>)

      // Record in history
      mockDb.insert<DCHistoryEntry>('dcHistory', {
        userId: game.userId,
        challengeTitle: card.challengeTitle,
        zone: card.zone,
        multiplier: card.multiplier,
        status: 'accepted',
        xpEarned,
      } as DCHistoryEntry)

      return updated!
    })
  },

  async passCards(gameId: UUID): Promise<ApiResponse<DealersChoiceGame>> {
    return safeExecute(() => {
      const game = mockDb.findById<DealersChoiceGame>('dealersChoiceGames', gameId)
      if (!game) throw new Error('Game not found')
      if (game.status !== 'revealed') throw new Error('Cards already decided')

      const updated = mockDb.update<DealersChoiceGame>('dealersChoiceGames', gameId, {
        status: 'passed',
      } as Partial<DealersChoiceGame>)

      // Record in history
      mockDb.insert<DCHistoryEntry>('dcHistory', {
        userId: game.userId,
        challengeTitle: 'Passed',
        zone: game.cards[0].zone,
        multiplier: 0,
        status: 'passed',
      } as DCHistoryEntry)

      return updated!
    })
  },

  async getCooldownStatus(): Promise<ApiResponse<{ onCooldown: boolean; endsAt?: string; remainingMs?: number }>> {
    return safeExecute(() => {
      const userId = getCurrentUserId()
      if (!userId) return { onCooldown: false }

      const lastGame = mockDb.findWhere<DealersChoiceGame>(
        'dealersChoiceGames',
        (g) => g.userId === userId
      ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]

      if (!lastGame?.cooldownEndsAt) return { onCooldown: false }

      const endsAt = new Date(lastGame.cooldownEndsAt)
      const now = new Date()

      if (endsAt > now) {
        return {
          onCooldown: true,
          endsAt: lastGame.cooldownEndsAt,
          remainingMs: endsAt.getTime() - now.getTime(),
        }
      }

      return { onCooldown: false }
    })
  },

  async getDCHistory(): Promise<ApiResponse<DCHistoryEntry[]>> {
    return safeExecute(() => {
      const userId = getCurrentUserId()
      if (!userId) throw new Error('Not authenticated')

      const history = mockDb.findWhere<DCHistoryEntry>(
        'dcHistory',
        (h) => h.userId === userId
      )
      history.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      return history
    })
  },
}
